import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeftIcon, GiftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

// Configuración de la campaña de reseñas de Google
// (llegás desde Redes → Reseñas o desde Fidelización → Cupones)
const ReviewsConfig: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/reviews')
      .then((res) => setConfig(res.data?.config || null))
      .catch((e) => toast.error(e.response?.data?.error || 'Error al cargar la configuración'))
      .finally(() => setLoading(false));
  }, []);

  const upd = (patch: any) => setConfig((prev: any) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put('/api/reviews/config', config);
      setConfig(res.data.config);
      toast.success('Configuración guardada');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" /></div>;
  }
  if (!config) {
    return <div className="p-6 text-muted-foreground">No se pudo cargar la configuración.</div>;
  }

  const inputCls = 'w-full h-9 px-2 rounded-lg border bg-background text-sm';

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Configuración de reseñas</h1>
          <p className="text-sm text-muted-foreground">Campaña de reseñas de Google: cupón, envío y textos de la página</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            Campaña
            <label className="flex items-center gap-2 cursor-pointer text-sm font-normal">
              <input
                type="checkbox"
                checked={Boolean(config.enabled)}
                onChange={(e) => upd({ enabled: e.target.checked })}
                className="h-4 w-4 rounded"
              />
              {config.enabled ? 'Activa' : 'Pausada'}
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Al confirmar el retiro de un vehículo se le manda al cliente UN pedido de reseña (una sola vez en la vida por cliente).
            Pausada, no se mandan más pedidos; lo ya enviado sigue funcionando.
          </p>
          <div className="w-48">
            <label className="text-xs text-muted-foreground">Envío (minutos después del retiro)</label>
            <input type="number" min={5} value={config.delayMinutes ?? 150} onChange={(e) => upd({ delayMinutes: e.target.value })} className={inputCls} />
            <p className="text-xs text-muted-foreground mt-1">Si cae de noche (19:30-9:30) se manda al día siguiente a las 10.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><GiftIcon className="h-4 w-4" />Cupón de descuento</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Tipo</label>
              <select value={config.courtesyType || 'percent'} onChange={(e) => upd({ courtesyType: e.target.value })} className={inputCls}>
                <option value="percent">Porcentaje (%)</option>
                <option value="amount">Monto fijo ($)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Valor</label>
              <input type="number" min={0} value={config.courtesyValue ?? 10} onChange={(e) => upd({ courtesyValue: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Vigencia (días)</label>
              <input type="number" min={1} value={config.courtesyDays ?? 90} onChange={(e) => upd({ courtesyDays: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Se aplica a</label>
              <select value={config.courtesyAppliesTo || 'all'} onChange={(e) => upd({ courtesyAppliesTo: e.target.value })} className={inputCls}>
                <option value="all">Todo (productos y servicios)</option>
                <option value="products">Solo productos</option>
                <option value="services">Solo servicios</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Monto mínimo de compra ($)</label>
              <input type="number" min={0} value={config.courtesyMinAmount ?? 0} onChange={(e) => upd({ courtesyMinAmount: e.target.value })} className={inputCls} />
              <p className="text-xs text-muted-foreground mt-1">0 = sin mínimo</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Tope del descuento ($)</label>
              <input type="number" min={0} value={config.courtesyMaxDiscount ?? 0} onChange={(e) => upd({ courtesyMaxDiscount: e.target.value })} className={inputCls} disabled={config.courtesyType === 'amount'} />
              <p className="text-xs text-muted-foreground mt-1">{config.courtesyType === 'amount' ? 'No aplica con monto fijo' : '0 = sin tope'}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            El cupón se crea automáticamente en Fidelización → Cupones al confirmarse la reseña (un solo uso).
            Los ya emitidos conservan sus condiciones.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><DocumentTextIcon className="h-4 w-4" />Textos de la página de reseña</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Título</label>
              <input value={config.landingTitle || ''} onChange={(e) => upd({ landingTitle: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Texto del botón</label>
              <input value={config.landingButton || ''} onChange={(e) => upd({ landingButton: e.target.value })} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground">Mensaje</label>
              <textarea value={config.landingMessage || ''} onChange={(e) => upd({ landingMessage: e.target.value })} rows={2} className="w-full px-2 py-1.5 rounded-lg border bg-background text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Nombre del negocio (pie)</label>
              <input value={config.landingBusiness || ''} onChange={(e) => upd({ landingBusiness: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">WhatsApp (54 9 ...)</label>
              <input value={config.landingWhatsapp || ''} onChange={(e) => upd({ landingWhatsapp: e.target.value })} className={inputCls} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Vista previa: <a className="underline" href="/r/demo123" target="_blank" rel="noopener noreferrer">app.rocam.com.ar/r/demo123</a>
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar configuración'}</Button>
      </div>
    </div>
  );
};

export default ReviewsConfig;
